import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Column } from 'react-table'

import { Box, Icon, Text, Tooltip, DateWithToolTip, Flex } from 'components'
import { faEnvelopeSquare } from '@fortawesome/pro-light-svg-icons/faEnvelopeSquare'

import FunnelXIcon from 'assets/funnel-x.svg'
import { LeadActivityType, ViewType } from 'types/global'
import { LeadInfo } from 'store/types'
import { LeadPriority } from 'utils/constants'

import { DropdownBadge } from '../shared'
import { Activities } from '../constant'
import { LeadColumnType } from '../types'
import { TableUiConfig } from '../index'

import {
  IconWrapper,
  LeadDetailsWrapper,
  DateTime,
  LeadOwner,
  LeadCampaign,
  LeadSource,
  StyledIcon,
  Status as StatusTextWrapper,
  LastActivity,
  AdditionalOwner,
  AdditionalOwnerCount,
  ConvertedBy,
  ClosedReason,
  ClosedDate,
  ClosedBy,
  LeadInfoWrapper,
  DeletedDate,
  DeletedReason,
  DeletedBy,
} from '../styles'
import { columnConfig, ConfigItem } from '../columnConfig'
import { FilterViewType } from '../Filters/constant'
import { DropdownBadgeType } from '../shared/Badges/variants'
import { ACTIVITY_TOOLTIP } from '../../Leads/LeadDetail/RightSection/ActivitiesTab/ActivityItemWrapper'
import { getLeadRoute } from 'utils/url'
import { useReusableTab } from 'hooks/use-reusable-lead-tab'

type Props = (
  tableUiConfig: TableUiConfig,
  view: ViewType,
  onLeadNameClick?: () => void,
) => { columns: Array<LeadColumnType> | undefined; defaultColumnIds: string[] | undefined }

export const getActivityIcon = (type: LeadActivityType) => {
  switch (type) {
    case 'Email (Log)':
      return <Icon color={'gray5'} marginRight={'5px'} fontSize={19} icon={faEnvelopeSquare} />
    default:
      return <StyledIcon icon={Activities[type]} />
  }
}

const useColumnDefinitions: Props = (tableUiConfig, view, onLeadNameClick) => {
  const isDeletedView = view === FilterViewType.Deleted

  const { t } = useTranslation()
  const { openInReusableTab } = useReusableTab()

  const columnDefinitions: { [key: string]: Column<LeadInfo> } = useMemo(
    () => ({
      leadName: {
        id: 'leadName',
        width: 200,
        Header: 'lead_information_title',
        accessor: 'leadName',
        Cell: ({ row }) => {
          const { color, icon } = iconMeta[view as ViewType]
          const leadURI = getLeadRoute({ leadId: row.original.leadId })

          const leadNameColumnwidth = [FilterViewType.Deleted, FilterViewType.All].includes(view as FilterViewType)
            ? '70%'
            : '100%'
          return (
            <LeadInfoWrapper
              to={leadURI}
              onClick={(e) => {
                e.preventDefault()
                onLeadNameClick?.()
                openInReusableTab(leadURI)
              }}
            >
              <Box display="flex">
                <Box
                  className="lead-name"
                  display="flex"
                  alignContent="center"
                  alignItems="center"
                  width={leadNameColumnwidth}
                >
                  {(view === FilterViewType.Converted ||
                    view === FilterViewType.Deleted ||
                    view === FilterViewType.Invalid) && (
                    <Box marginRight="10px">
                      <Icon icon={icon} color={color} />
                    </Box>
                  )}
                  {view === FilterViewType.Lost && <Box marginRight="10px">{icon}</Box>}
                  <div
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Tooltip title={row.original.leadName}>
                      <Text fontWeight="medium" variant="default" ellipsis>
                        {row.original.leadName}
                      </Text>
                    </Tooltip>
                  </div>
                </Box>

                {(row.original.isDeleted || view === FilterViewType.Deleted) && (
                  <Box className="lead-status" display="flex" width="30%" justifyContent="flex-end" alignItems="center">
                    {t('deleted_lbl')}
                  </Box>
                )}
              </Box>
              {!tableUiConfig.isDenseView && (
                <LeadDetailsWrapper id="lead-details-wrapper">
                  <IconWrapper>
                    <Tooltip title="location_lbl" position="bottom">
                      <StyledIcon icon={['far', 'map-marker-alt']} color="gray5" />
                    </Tooltip>
                    <Text ellipsis>{row.original.leadLocation || '-'}</Text>
                  </IconWrapper>
                  <IconWrapper>
                    <Tooltip title="industry_one" position="bottom">
                      <StyledIcon icon={['far', 'building']} color="gray5" />
                    </Tooltip>
                    <Text ellipsis>{row.original.industry || '-'}</Text>
                  </IconWrapper>
                </LeadDetailsWrapper>
              )}
            </LeadInfoWrapper>
          )
        },
      },
      leadType: {
        id: 'leadType',
        Header: 'type_lbl',
        accessor: 'leadType',
        Cell: ({ row }) => (row.original?.leadType ? row.original.leadType : '-'),
      },
      priority: {
        id: 'priority',
        Header: 'priority_lbl',
        accessor: 'priority',
        Cell: ({ row }) =>
          row.original.priority ? (
            <DropdownBadge
              isDeletedView={isDeletedView}
              type={DropdownBadgeType.leadPriority}
              value={LeadPriority[row.original.priority]}
            />
          ) : (
            '-'
          ),
      },
      status: {
        id: 'status',
        Header: 'status_lbl',
        accessor: 'status',
        Cell: ({ row }) => <StatusTextWrapper>{row.original.status || '-'}</StatusTextWrapper>,
      },
      leadInDate: {
        id: 'leadInDate',
        Header: 'lead_in_date_title',
        accessor: 'leadInDate',
        Cell: ({ row }) => (
          <DateTime>
            <DateWithToolTip date={row.original.leadInDate} />
          </DateTime>
        ),
      },
      closedDate: {
        id: 'closedDate',
        Header: 'closed_date_lbl',
        accessor: 'closedDate',
        Cell: ({ row }) => (
          <ClosedDate>
            <DateWithToolTip date={row.original.closedDate} />
          </ClosedDate>
        ),
      },
      closedReason: {
        id: 'closedReason',
        Header: 'closed_reason_title',
        accessor: 'closedReason',
        Cell: ({ row }) => <ClosedReason>{row.original.closedReason || '-'}</ClosedReason>,
      },
      closedBy: {
        id: 'closedBy',
        Header: 'closed_by_lbl',
        accessor: 'closedBy',
        Cell: ({ row }) => {
          const { closedBy } = row.original
          return closedBy ? <ClosedBy>{row.original.closedBy}</ClosedBy> : '-'
        },
      },
      closedReasonDetail: {
        id: 'closedReasonDetail',
        Header: 'closed_reason_detail_title',
        accessor: 'closedReasonDetail',
        Cell: ({ row }) => <ClosedReason>{row.original.closedReason || '-'}</ClosedReason>,
      },
      deletedDate: {
        id: 'deletedDate',
        Header: 'deleted_date_lbl',
        accessor: 'deletedDate',
        Cell: ({ row }) => (
          <DeletedDate>
            <DateWithToolTip date={row.original.deletedDate} />
          </DeletedDate>
        ),
      },
      deletedReason: {
        id: 'deletedReason',
        Header: 'deleted_reason_lbl',
        accessor: 'deletedReason',
        Cell: ({ row }) => <DeletedReason>{row.original.deletedReason || '-'}</DeletedReason>,
      },
      deletedBy: {
        id: 'deletedBy',
        Header: 'deleted_by_lbl',
        accessor: 'deletedBy',
        Cell: ({ row }) => {
          const { deletedBy } = row.original
          return deletedBy ? <DeletedBy>{row.original.deletedBy}</DeletedBy> : '-'
        },
      },
      leadCampaign: {
        id: 'leadCampaign',
        Header: 'campaign_source_lbl',
        accessor: 'leadCampaign',
        Cell: ({ row }) => (
          <div>
            <LeadCampaign>{row.original.leadCampaign || '-'}</LeadCampaign>
            <LeadSource>
              {row.original.leadSource || '-'}
              {row.original.leadSource && row.original.leadSourceTypeName && ` (${row.original.leadSourceTypeName})`}
            </LeadSource>
          </div>
        ),
      },
      totalMarketPotential: {
        id: 'totalMarketPotential',
        Header: 'market_potential_title_lbl',
        accessor: 'totalMarketPotential',
        Cell: ({ row }) => (row.original?.totalMarketPotential ? row.original?.totalMarketPotential : '-'),
      },
      leadOwner: {
        id: 'leadOwner',
        Header: 'lead_owner_title_lbl',
        accessor: 'leadOwner',
        Cell: ({ row }) => (
          <LeadOwner color={row.original.leadOwner || row.original.leadOwnerTeam ? 'defaultText' : 'red'}>
            {!row.original.leadOwner && row.original.leadOwnerTeam && (
              <Icon color="gray5" icon={['far', 'users']} mr="8px" />
            )}
            {row.original.leadOwner || row.original.leadOwnerTeam || t('unassigned_lbl')}
          </LeadOwner>
        ),
      },
      additionalOwner: {
        id: 'additionalOwner',
        Header: 'collaborators_lbl',
        accessor: 'collaborators',
        Cell: ({ row }) => {
          const { additionalOwner, otherAdditionalOwnerNames, otherAdditionalOwnerCount } = row.original.collaborators
          return additionalOwner ? (
            <Tooltip title={otherAdditionalOwnerNames} position="bottom" textWidth="200px">
              <AdditionalOwner>{additionalOwner}</AdditionalOwner>
              {otherAdditionalOwnerCount > 0 && (
                <AdditionalOwnerCount>{`${otherAdditionalOwnerCount} ${
                  otherAdditionalOwnerCount > 1 ? 'others' : 'other'
                }`}</AdditionalOwnerCount>
              )}
            </Tooltip>
          ) : (
            '-'
          )
        },
      },
      convertedDate: {
        id: 'convertedDate',
        Header: 'converted_date_title',
        accessor: 'convertedDate',
        Cell: ({ row }) => (
          <DateTime>
            <DateWithToolTip date={row.original.convertedDate} />
          </DateTime>
        ),
      },
      convertedBy: {
        id: 'convertedBy',
        Header: 'converted_by_title',
        accessor: 'convertedBy',
        Cell: ({ row }) => {
          const { convertedBy } = row.original
          return convertedBy ? <ConvertedBy>{row.original.convertedBy}</ConvertedBy> : '-'
        },
      },
      lastActivity: {
        id: 'lastActivity',
        Header: 'last_activity_title',
        accessor: 'lastActivity',
        Cell: ({ row }) =>
          row.original?.lastActivity ? (
            <LastActivity>
              <Box display="flex" mb="0.2rem">
                {row.original.lastActivity.lastActivityType && (
                  <Tooltip
                    textTransform="capitalize"
                    title={ACTIVITY_TOOLTIP[row.original.lastActivity.lastActivityType]}
                    position="bottom"
                  >
                    {getActivityIcon(row.original.lastActivity.lastActivityType)}
                  </Tooltip>
                )}

                <DateTime>
                  <DateWithToolTip date={row.original.lastActivity?.lastActivityTs} />
                </DateTime>
              </Box>
              <Tooltip title={row.original.lastActivity?.lastActivityStatus} position="bottom">
                <Text>{row.original.lastActivity?.lastActivityStatus}</Text>
              </Tooltip>
            </LastActivity>
          ) : (
            '-'
          ),
      },
      lastActivityOutcome: {
        id: 'lastActivityOutcome',
        Header: 'last_activity_outcome',
        accessor: 'lastActivityOutcome',
        Cell: ({ row }) => (row.original?.lastActivityOutcome ? row.original?.lastActivityOutcome : '-'),
      },
      registrationNumber: {
        id: 'registrationNumber',
        Header: 'reg_no_lbl',
        accessor: 'registrationNumber',
        Cell: ({ row }) => (row.original?.registrationNumber ? row.original.registrationNumber : '-'),
      },
      recycledDateAndValue: {
        id: 'recycledDateAndValue',
        Header: 'last_recycled_date_lbl',
        accessor: 'recycledDateAndValue',
        Cell: ({ row }) =>
          row.original?.recycledStatus ? (
            <Flex flexDirection={'column'} gap="5px" alignItems={'left'}>
              <Text fontSize={'14px'}>recycled</Text>
              <DateWithToolTip date={row.original?.recycledDate} fontSize={'12px'} color="gray6" />
            </Flex>
          ) : (
            '-'
          ),
      },
      nextActivityDate: {
        id: 'nextActivityDate',
        Header: 'next_activity_date_lbl',
        accessor: 'nextActivityDate',
        Cell: ({ row }) =>
          row.original?.nextActivity ? (
            <LastActivity>
              <Box display="flex" mb="0.2rem">
                {row.original.nextActivity.nextActivityType && Activities[row.original.nextActivity.nextActivityType] && (
                  <Tooltip
                    textTransform="capitalize"
                    title={ACTIVITY_TOOLTIP[row.original.nextActivity.nextActivityType]}
                    position="bottom"
                  >
                    <StyledIcon icon={Activities[row.original.nextActivity.nextActivityType]} />
                  </Tooltip>
                )}
                <DateTime>
                  <DateWithToolTip date={row.original.nextActivity?.nextActivityTs} />
                </DateTime>
              </Box>
            </LastActivity>
          ) : (
            '-'
          ),
      },
      closedReasonDetails: {
        id: 'closedReasonDetails',
        Header: 'closed_reason_details_lbl',
        accessor: 'closedReasonDetails',
        Cell: ({ row }) => (row.original?.closedReasonDetails ? row.original.closedReasonDetails : '-'),
      },
      totalCalls: {
        id: 'totalCalls',
        Header: 'total_calls_lbl',
        accessor: 'totalCalls',
        Cell: ({ row }) => (row.original?.totalCalls ? row.original?.totalCalls : '-'),
      },
    }),
    [view, isDeletedView, tableUiConfig.isDenseView, t, onLeadNameClick, openInReusableTab],
  )

  const { columns, defaultColumnIds } = useMemo(() => {
    const currentView = view.toUpperCase() as keyof typeof columnConfig
    const config = columnConfig[currentView]
    const columns: LeadColumnType[] = []
    const defaultColumnIds: string[] = []

    config.forEach((column: ConfigItem) => {
      const { key, isHidden, isFixed } = column
      columns.push({ ...columnDefinitions[key], isDisabled: isFixed })
      if (!isHidden) {
        defaultColumnIds.push(key)
      }
    })

    return { columns, defaultColumnIds }
  }, [columnDefinitions, view])

  return { columns, defaultColumnIds }
}

const LostLeadIcon = styled(FunnelXIcon)`
  width: 100%;
  height: 100%;
`

const iconMeta: { [key in ViewType]: { icon: Array<string> | React.ReactNode; color: string } } = {
  ALL: { color: '', icon: null },
  CONVERTED: { color: 'green', icon: ['far', 'trophy-alt'] },
  DELETED: { color: 'gray', icon: ['fal', 'file-excel'] },
  INVALID: { color: 'yellow1', icon: ['fal', 'phone-slash'] },
  LOST: { color: 'green', icon: <LostLeadIcon width="22" height="22" viewBox="0 0 20 10" /> },
  OPEN: { color: '', icon: null },
}

export default useColumnDefinitions
